// externals
import React from 'react';
import { useSelector } from 'react-redux';

// hooks
import { useFetchVehicleDetails, useVehicleDetails } from '../../hooks';
import { HeroRow, BottomRow, VehicleDetails, VehicleDetailsWrapper } from './VehicleDetailsContainer.style';
import { FuelEconomySection } from '../FuelEconomySection/FuelEconomySection';
import { HeroSection } from '../HeroSection/HeroSection';
import { PackagesSection } from '../PackagesSection/PackagesSection';
import { SpecificationsSection } from '../SpecificationsSection/SpecificationsSection';
import { WarrantySection } from '../WarrantySection/WarrantySection';
import { vehicleDetailsSelectors } from '../../store/config';

/**
 * VehicleDetailsContainer - Main container component for the Vehicle Details Page.
 *
 * Vehicle identity (VIN/dealerId for stock, YMMT for custom) is resolved inside
 * useVehicleDetails via additional selectors.
 */
export const VehicleDetailsContainer: React.FC = () => {
    useFetchVehicleDetails();

    const { isLoading, error, hasData, vehicleDetails } = useVehicleDetails();
    // Which sections this host allows, one answer per section.
    const sections = useSelector(vehicleDetailsSelectors.additionalSelectors.getVehicleSections);

    // While loading, render nothing here - the app-level loaderConfig displays the loader.
    // This prevents the empty/"no data" state from flashing in the background.
    if (isLoading) {
        return null;
    }

    if (!hasData) {
        return (
            <VehicleDetails data-testid="vehicle-details-container-empty">
                <div style={{ padding: '2rem', margin: '0 auto' }}>No vehicle data available.</div>
            </VehicleDetails>
        );
    }

    if (error) {
        return (
            <VehicleDetails data-testid="vehicle-details-container-error">
                <div style={{ padding: '2rem', color: 'red', margin: '0 auto' }}>Error loading vehicle details: {error}</div>
            </VehicleDetails>
        );
    }

    const { specifications, fuelEconomy, warrantyData, packagesEquipment } = vehicleDetails;

    return (
        <VehicleDetailsWrapper data-testid="vehicle-details-container">
            <VehicleDetails $bgColor="transparent" data-testid="vehicle-details-container">
                {/* Hero + Specifications side-by-side */}
                <HeroRow>
                    {sections.images && <HeroSection vehicleData={specifications} />}
                    {sections.details && <SpecificationsSection vehicle={specifications} />}
                </HeroRow>

                {/* Packages & Equipment */}
                {sections.optionalEquipment && <PackagesSection packagesEquipment={packagesEquipment} />}

                <BottomRow>
                    {sections.estimatedMPG && fuelEconomy.hasFuelEconomy ? <FuelEconomySection fuelEconomy={fuelEconomy} /> : null}
                    {sections.warranty && warrantyData.shouldShowWarranties && (
                        <WarrantySection
                            warranties={warrantyData.warranties}
                            inServiceDateLabel={warrantyData.inServiceDateLabel}
                        />
                    )}
                </BottomRow>
            </VehicleDetails>
        </VehicleDetailsWrapper>
    );
};
